/**
 * Painel informativo de pedido
 * Exibe informações sobre o período de pedido ativo
 * Busca dados automaticamente da sessão do vendedor
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, IconButton, Tooltip, Badge } from '@mui/material';
import { ShoppingCart, ShoppingBasket, Inventory } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@hooks';
import { ROUTES } from '@types';

export const PainelPedido: React.FC = () => {
  const { vendedor } = useSession();
  const navigate = useNavigate();
  const [tempoRestante, setTempoRestante] = useState<string>('');
  const [totalItems, setTotalItems] = useState<number>(0);

  // Extrai dados da sessão
  const indPrevisao = vendedor?.indPrevisao || 0;
  const indPrevisaoId = vendedor?.indPrevisaoId || 0;
  const indPrevisaoConfirma = vendedor?.indPrevisaoConfirma || '';
  const indPedido = vendedor?.indPedido || 0;

  // Busca total de itens no carrinho do localStorage
  useEffect(() => {
    const updateTotalItems = () => {
      try {
        const carrinhoData = localStorage.getItem('carrinho');
        if (carrinhoData) {
          const carrinho = JSON.parse(carrinhoData);
          setTotalItems(carrinho.quantidadeTotal || 0);
        } else {
          setTotalItems(0);
        }
      } catch (error) {
        console.error('Erro ao ler carrinho do localStorage:', error);
        setTotalItems(0);
      }
    };

    // Atualiza imediatamente
    updateTotalItems();

    // Atualiza quando o localStorage mudar
    window.addEventListener('storage', updateTotalItems);

    // Atualiza a cada 1 segundo para mudanças na mesma aba
    const interval = setInterval(updateTotalItems, 1000);

    return () => {
      window.removeEventListener('storage', updateTotalItems);
      clearInterval(interval);
    };
  }, []);

  // Define indPedidoFim como data corrente na hora configurada em VITE_PEDIDO_HORA_FIM
  const getDataCorrentePedido = (hora: number): string => {
    const agora = new Date();
    agora.setHours(hora, 0, 0, 0);

    const dia = String(agora.getDate()).padStart(2, '0');
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    const ano = agora.getFullYear();
    const hh = String(hora).padStart(2, '0');

    return `${dia}/${mes}/${ano} ${hh}:00:00`;
  };

  const pedidoHoraFim = parseInt(import.meta.env.VITE_PEDIDO_HORA_FIM ?? '18', 10);

  // Data encerramento de pedidos
  const indPedidoFim = getDataCorrentePedido(pedidoHoraFim);

  // Converte string de data brasileira (DD/MM/YYYY HH:MM:SS) para objeto Date
  const parseDataBrasileira = (dataStr: string): Date => {
    if (!dataStr) return new Date();

    // Formato esperado: DD/MM/YYYY HH:MM:SS ou DD/MM/YYYY HH:MM
    const partes = dataStr.split(' ');
    const [dia, mes, ano] = partes[0].split('/');
    const hora = partes[1] || '00:00:00';
    const [hh, mm, ss = '00'] = hora.split(':');

    // Cria data no formato correto (ano, mes-1, dia, hora, minuto, segundo)
    return new Date(
      parseInt(ano),
      parseInt(mes) - 1, // Mês começa em 0
      parseInt(dia),
      parseInt(hh),
      parseInt(mm),
      parseInt(ss)
    );
  };

  // Calcula o tempo restante
  useEffect(() => {
    if (!indPedidoFim) return;

    const calcularTempoRestante = () => {
      const agora = new Date();
      const dataFim = parseDataBrasileira(indPedidoFim);

      const diffMs = dataFim.getTime() - agora.getTime();

      if (diffMs <= 0) {
        setTempoRestante('Finalizado');
        return;
      }

      const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const horas = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const segundos = Math.floor((diffMs % (1000 * 60)) / 1000);

      let tempo = '';
      if (dias > 0) {
        tempo = `${dias}d ${horas}h ${minutos}m ${segundos}s`;
      } else if (horas > 0) {
        tempo = `${horas}h ${minutos}m ${segundos}s`;
      } else if (minutos > 0) {
        tempo = `${minutos}m ${segundos}s`;
      } else {
        tempo = `${segundos}s`;
      }

      setTempoRestante(tempo);
    };

    // Calcula imediatamente
    calcularTempoRestante();

    // Atualiza a cada segundo
    const interval = setInterval(calcularTempoRestante, 1000);

    return () => clearInterval(interval);
  }, [indPedidoFim]);

  // Formata data para exibição (já vem no formato DD/MM/YYYY HH:MM:SS)
  const formatarData = (data: string): string => {
    if (!data) return '';

    // Se a data já está no formato brasileiro, apenas retorna
    // Formato esperado: DD/MM/YYYY HH:MM:SS ou DD/MM/YYYY HH:MM
    const partes = data.split(' ');
    if (partes.length >= 2) {
      const dataFormatada = partes[0]; // DD/MM/YYYY
      const horaFormatada = partes[1].substring(0, 5); // HH:MM
      return `${dataFormatada} ${horaFormatada}`;
    }

    return data;
  };

  // Handler para navegar à lista de itens da previsão
  const handleItensPrevisaoClick = () => {
    navigate(ROUTES.ITENS_PREVISAO);
  };

  // Handler para navegar ao carrinho do pedido
  const handleCarrinhoPedidoClick = () => {
    navigate(ROUTES.CARRINHO_PEDIDO);
  };

  // Não exibe o painel se não houver previsão ativa ou pedido inativo
  if (indPrevisao <= 0 || indPedido === 0) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        top: { xs: 50, sm: 20 },
        left: '50%',
        transform: 'translateX(-50%)',
        width: { xs: '100%', sm: '90%', md: '85%', lg: '1200px' },
        maxWidth: 'lg',
        zIndex: 1200,
        borderRadius: { xs: 0, sm: 2 },
        borderTop: { xs: 0, sm: `5px solid #026333` },
        backgroundColor: 'background.paper',
        transition: 'box-shadow 0.3s',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <Grid container alignItems="center" sx={{ p: { xs: 1.5, sm: 2 } }}>
        {/* Coluna 1: Ícone de Previsão */}
        <Grid
          item
          xs={2}
          sm={1.5}
          md={1.5}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ShoppingBasket
            sx={{
              fontSize: { xs: 32, sm: 40, md: 48 },
              color: 'success.main',
            }}
          />
        </Grid>

        {/* Coluna 2: Informações */}
        <Grid item xs={6} sm={7.5} md={8}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 0.3, sm: 0.5 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                Início::
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                {formatarData(indPrevisaoConfirma)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                Fim:
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                {formatarData(indPedidoFim)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                Tempo Restante:
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                color={tempoRestante === 'Finalizado' ? 'error' : 'primary'}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem', md: '1rem' } }}
              >
                {tempoRestante}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Coluna 3: Ícone de Produtos */}
        <Grid
          item
          xs={2}
          sm={1}
          md={1}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Tooltip title="Ver todos os itens da previsão" arrow>
            <IconButton
              onClick={handleItensPrevisaoClick}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Inventory
                sx={{
                  fontSize: { xs: 28, sm: 36, md: 42 },
                }}
              />
            </IconButton>
          </Tooltip>
        </Grid>

        {/* Coluna 4: Ícone de Carrinho */}
        <Grid
          item
          xs={2}
          sm={1.5}
          md={1.5}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Tooltip title="Ver carrinho de pedido" arrow>
            <IconButton
              onClick={handleCarrinhoPedidoClick}
              sx={{
                color: indPrevisaoId > 0 ? 'success.main' : 'action.disabled',
                cursor: indPrevisaoId > 0 ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
              disableRipple
            >
              <Badge
                badgeContent={totalItems}
                color="error"
                max={99}
                showZero={false}
              >
                <ShoppingCart
                  sx={{
                    fontSize: { xs: 32, sm: 40, md: 48 },
                  }}
                />
              </Badge>
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </Paper>
  );
};
